import React from "react";
import { motion } from "framer-motion";
import { PARTNERSHIP_BEATS } from "../data/content";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

const Story = () => {
  return (
    <section
      id="story"
      data-testid="story-section"
      className="relative py-32 md:py-56 bg-[#14100D] text-[#EFE7D6] overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-8 md:px-16">
        <motion.div {...fadeUp} className="mb-20 md:mb-32">
          <div className="flex items-center gap-6 mb-10">
            <span className="counter">03 — Heritage</span>
            <span className="w-16 hairline-strong h-px inline-block" />
            <span className="overline text-[#EFE7D6]/60">The Story</span>
          </div>
          <h2
            className="display-tight text-[3rem] sm:text-6xl md:text-8xl max-w-5xl"
            data-testid="story-headline"
          >
            A flavor
            <br />
            <span className="italic font-light text-[#DCCFB4]">fifty-five years</span>
            <br />
            in the making.
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-12 gap-12 md:gap-24">
          {/* Editorial narrative */}
          <div className="md:col-span-5">
            <div className="md:sticky md:top-32 space-y-10">
              <motion.p
                {...fadeUp}
                className="font-body text-2xl md:text-3xl leading-[1.35] text-[#EFE7D6]"
              >
                In 1971, a creamery worker at Meadow Gold blended three island
                fruits — passion fruit, orange, guava — and quietly invented one
                of the most beloved flavors in the Pacific.
              </motion.p>
              <span className="hairline block" />
              <motion.p
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.1 }}
                className="font-body text-[#DCCFB4]/85 text-base md:text-lg leading-[1.75] font-light"
              >
                Every kid in Hawai‘i grew up with POG. Every kitchen had a
                carton. Every plate lunch came with one. It is one of the rarest
                things in food &amp; beverage — a regional flavor with
                <em> universal recognition</em>.
              </motion.p>
              <motion.p
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.2 }}
                className="font-body text-[#DCCFB4]/85 text-base md:text-lg leading-[1.75] font-light"
              >
                In 2026, POG Spirits — an independent spirits brand created in
                partnership with Meadow Gold (the islands' beverage house since
                1897 and inventor of POG in 1971) — does something no one had
                done before: brings POG into the bottle.
              </motion.p>
            </div>
          </div>

          {/* Timeline */}
          <div className="md:col-span-7">
            <div className="space-y-0">
              {PARTNERSHIP_BEATS.map((b, i) => (
                <motion.div
                  key={b.year}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                  className="grid grid-cols-12 gap-6 md:gap-8 py-10 md:py-14 border-t border-[#EFE7D6]/12"
                  data-testid={`timeline-beat-${b.year}`}
                >
                  <div className="col-span-12 sm:col-span-3">
                    <div className="font-display text-5xl md:text-6xl text-[#E85A15] leading-none tracking-tighter">
                      {b.year}
                    </div>
                  </div>
                  <div className="col-span-12 sm:col-span-9">
                    <h3 className="font-display text-2xl md:text-3xl text-[#EFE7D6] mb-4 tracking-tight">
                      {b.title}
                    </h3>
                    <p className="font-body text-[#DCCFB4]/80 leading-[1.75] text-base md:text-lg font-light">
                      {b.body}
                    </p>
                  </div>
                </motion.div>
              ))}
              <span className="hairline block" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Story;
